import axios from 'axios-observable';
import { AxiosObservable } from 'axios-observable/dist/axios-observable.interface';
import { CommandRequest } from '../../helpers/request/models';
import CONFIG from '../../settings';
import { PageableQuery, UUID } from '../models';
import { TalosResponse } from '../app/basicModels';
import { ExtraDimension, ExtraDimensionPage } from './models';
import { buildWorkspaceConfig } from '../methodologyDeep/scope';
import { concatMap, map } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { hasOperation, ObservationsConfigV2 } from '../observationsConfigV2';

export const EXTRA_DIMENSION_PAGE_SIZE = 50;
export const extraDimenstionsUrl = `${CONFIG.apiUrl}:${CONFIG.demeterPort}`;
export const createExtraDimension = (request: CommandRequest<ExtraDimension>, migratedWrites = '[]'): AxiosObservable<TalosResponse> => {
  if (hasOperation(migratedWrites, 'extra-dimensions.create')) {
    return ObservationsConfigV2.createExtraDimension(request.payload);
  }

  const objToSave = {
    account: request.account,
    payload: {
      id: request.payload.id,
      company_id: request.payload.company_id,
      workspace_id: request.payload.workspace_id,
      name: request.payload.name,
      description: request.payload.description,
      required: request.payload.required,
      values: request.payload.values
    }
  };
  return axios.post<TalosResponse>(
    `${extraDimenstionsUrl}/api/v1/extra-dimensions`,
    objToSave,
    buildWorkspaceConfig(request.payload.workspace_id ?? undefined)
  );
};

export const updateExtraDimension = (request: CommandRequest<ExtraDimension>, migratedWrites = '[]'): AxiosObservable<TalosResponse> => {
  if (hasOperation(migratedWrites, 'extra-dimensions.update')) {
    return ObservationsConfigV2.updateExtraDimension(request.payload);
  }

  const objToSave = {
    account: request.account,
    payload: {
      id: request.payload.id,
      company_id: request.payload.company_id,
      workspace_id: request.payload.workspace_id,
      name: request.payload.name,
      description: request.payload.description,
      required: request.payload.required,
      values: request.payload.values
    }
  };
  return axios.put<TalosResponse>(
    `${extraDimenstionsUrl}/api/v1/extra-dimensions`,
    objToSave,
    buildWorkspaceConfig(request.payload.workspace_id ?? undefined)
  );
};

export const loadExtraDimension = (id: UUID): AxiosObservable<any> => {
  return axios.get<any>(`${extraDimenstionsUrl}/api/v1/extra-dimensions/${id}`);
};

const loadExtraDimensionsByCompanyPage = (companyId: UUID, query: PageableQuery) => {
  return axios
    .get<ExtraDimensionPage>(`${extraDimenstionsUrl}/api/v1/companies/${companyId}/extra-dimensions`, {
      params: { page: query.pageable.page, size: query.pageable.size }
    })
    .pipe(map(m => m.data));
};

export const loadAllExtraDimensionsByCompany = (companyId: UUID) => {
  return loadExtraDimensionsByCompanyPage(companyId, { pageable: { page: 0, size: EXTRA_DIMENSION_PAGE_SIZE } }).pipe(
    concatMap(({ content, total_pages }) => {
      const pages = Array.from({ length: total_pages - 1 }, (_, i) => i + 1);
      const observables = pages.map(p =>
        loadExtraDimensionsByCompanyPage(companyId, {
          pageable: {
            page: p,
            size: EXTRA_DIMENSION_PAGE_SIZE
          }
        }).pipe(map(page => page.content))
      );
      return forkJoin([of(content), ...observables]);
    }),
    map(extraDimensions => ({ data: { content: extraDimensions.flat() } }))
  );
};

const loadExtraDimensionsByWorkspacePage = (workspaceId: UUID, query: PageableQuery) => {
  return axios
    .get<ExtraDimensionPage>(`${extraDimenstionsUrl}/api/v1/workspaces/${workspaceId}/extra-dimensions`, {
      params: { page: query.pageable.page, size: query.pageable.size }
    })
    .pipe(map(m => m.data));
};

export const loadAllExtraDimensionsByWorkspace = (workspaceId: UUID) => {
  return loadExtraDimensionsByWorkspacePage(workspaceId, { pageable: { page: 0, size: EXTRA_DIMENSION_PAGE_SIZE } }).pipe(
    concatMap(({ content, total_pages }) => {
      const pages = Array.from({ length: total_pages - 1 }, (_, i) => i + 1);
      const observables = pages.map(p =>
        loadExtraDimensionsByWorkspacePage(workspaceId, {
          pageable: {
            page: p,
            size: EXTRA_DIMENSION_PAGE_SIZE
          }
        }).pipe(map(page => page.content))
      );
      return forkJoin([of(content), ...observables]);
    }),
    map(extraDimensions => ({ data: { content: extraDimensions.flat() } }))
  );
};
